// src/pages/PrivacyPolicyPage.js
import React from 'react';
import LegalLayout from '../components/LegalLayout';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'google-user-data', title: 'Google User Data' },
  { id: 'how-we-use', title: 'How We Use Your Data' },
  { id: 'data-sharing', title: 'Data Sharing & Disclosure' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'cookies-tracking', title: 'Cookies & Tracking' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' }
];

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="January 1, 2024"
      sections={sections}
    >
      <section id="introduction">
        <h2>Introduction</h2>
        <p>
          At BloomPal, we are committed to protecting the privacy and security of your
          personal and health information. This policy explains how we collect, use,
          share, and safeguard your data in compliance with HIPAA and other privacy laws.
        </p>
        <p>
          By using BloomPal, you agree to the collection and use of information in
          accordance with this policy. We are dedicated to maintaining your trust and
          ensuring transparency about how we handle your data.
        </p>
      </section>

      <section id="information-we-collect">
        <h2>Information We Collect</h2>
        <p>
          We collect information that you provide directly to us, as well as information
          that we obtain automatically when you use our services.
        </p>
        
        <h3>Essential Data</h3>
        <ul>
          <li><strong>Account Information:</strong> Name, email address, and other
            information you provide when creating an account.</li>
          <li><strong>Session Data:</strong> Information about your login sessions and
            authentication tokens.</li>
        </ul>

        <h3>Treatment Data</h3>
        <ul>
          <li><strong>Health-Related Information:</strong> Information you provide to
            receive mental health services and support, including mood tracking data,
            wellness activities, and journal entries.</li>
          <li><strong>Activity Data:</strong> Records of your interactions with wellness
            features and mindfulness activities.</li>
        </ul>

        <h3>Google User Data</h3>
        <p>
          When you choose to connect your Google account, we access the following
          information:
        </p>
        <ul>
          <li><strong>Basic Profile Information:</strong> Your name and email address
            for account authentication and personalization.</li>
          <li><strong>Calendar Data:</strong> Access to your Google Calendar to enable
            features like mindful scheduling and activity reminders.</li>
        </ul>
        <p>
          We only access Google data that is necessary for the features you choose to
          use. You can revoke access to your Google account at any time through your
          account settings or Google's security settings.
        </p>

        <h3>Optional Data</h3>
        <ul>
          <li><strong>Usage Analytics:</strong> Information about how you interact with
            our platform (only if you opt-in).</li>
          <li><strong>Communications Preferences:</strong> Your preferences for receiving
            updates and notifications.</li>
          <li><strong>Research Participation:</strong> Data shared only if you explicitly
            opt-in to research participation.</li>
        </ul>
      </section>

      <section id="google-user-data">
        <h2>Google User Data</h2>
        <p>
          <strong>Our app accesses Google user data</strong> (specifically Calendar data
          and Basic Profile information) to provide core features like Mindful Scheduling.
        </p>
        
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '20px',
          margin: '24px 0'
        }}>
          <h3 style={{ marginTop: 0, color: '#1a8990' }}>Limited Use Disclosure</h3>
          <p style={{ marginBottom: '12px' }}>
            <strong>We do not use Google Workspace data for advertising purposes.</strong>
          </p>
          <p style={{ marginBottom: '12px' }}>
            <strong>We do not use Google Workspace data to train generalized AI models.</strong>
          </p>
          <p style={{ marginBottom: 0 }}>
            BloomPal's use and transfer to any other app of information received from
            Google APIs will adhere to the <strong>Google API Services User Data Policy</strong>,
            including the <strong>Limited Use</strong> requirements.
          </p>
        </div>

        <h3>How We Use Google Data</h3>
        <ul>
          <li><strong>Calendar Integration:</strong> We use your Google Calendar data
            to help you schedule wellness activities at optimal times, avoid conflicts,
            and maintain a balanced routine.</li>
          <li><strong>Authentication:</strong> We use your basic profile information
            (name and email) to create and maintain your BloomPal account.</li>
          <li><strong>No Sharing:</strong> We do not share your Google data with third
            parties except as required by law or as necessary to provide our services
            (e.g., cloud infrastructure providers under strict confidentiality agreements).</li>
        </ul>

        <h3>Your Control Over Google Data</h3>
        <ul>
          <li>You can disconnect your Google account at any time through your BloomPal
            account settings.</li>
          <li>You can revoke access through Google's security settings at
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
              {' '}myaccount.google.com/permissions
            </a>.
          </li>
          <li>When you disconnect, we will stop accessing new Google data, but we may
            retain previously accessed data as necessary to maintain your account and
            comply with legal obligations.</li>
        </ul>
      </section>

      <section id="how-we-use">
        <h2>How We Use Your Data</h2>
        
        <h3>Essential Uses</h3>
        <ul>
          <li><strong>Core Functionality:</strong> To provide and maintain our wellness
            platform and services.</li>
          <li><strong>Secure Login:</strong> To authenticate your identity and secure
            your account.</li>
          <li><strong>HIPAA-Compliant Treatment Sessions:</strong> To enable and deliver
            mental health services and support.</li>
        </ul>

        <h3>Treatment Uses</h3>
        <ul>
          <li><strong>Personalized Therapy Plans:</strong> To create and adjust wellness
            recommendations based on your needs.</li>
          <li><strong>Progress Tracking:</strong> To help you monitor your wellness
            journey and identify patterns.</li>
          <li><strong>Mindful Scheduling:</strong> To suggest optimal times for wellness
            activities based on your calendar availability.</li>
        </ul>

        <h3>Analytics & Research</h3>
        <ul>
          <li>To improve our service quality and user experience (only if you opt-in).</li>
          <li>To conduct research that may benefit mental health and wellness (only
            with your explicit consent).</li>
        </ul>

        <h3>Communications</h3>
        <ul>
          <li>To send you updates, reminders, or engagement messages (only if you consent).</li>
          <li>To respond to your inquiries and provide customer support.</li>
        </ul>
      </section>

      <section id="data-sharing">
        <h2>Data Sharing & Disclosure</h2>
        <p>
          <strong>We do not sell your data.</strong> We are committed to protecting your
          privacy and only share information as described below.
        </p>

        <h3>Permitted Disclosures</h3>
        <ul>
          <li><strong>HIPAA-Compliant Sharing:</strong> We may share Protected Health
            Information (PHI) only as permitted by HIPAA, such as with healthcare
            providers involved in your care or as required by law.</li>
          <li><strong>Service Providers:</strong> We may share data with trusted service
            providers who assist us in operating our platform (e.g., cloud hosting,
            analytics). These providers are bound by strict confidentiality agreements
            and are only permitted to use your data to provide services to us.</li>
          <li><strong>Legal Requirements:</strong> We may disclose information if required
            by law, court order, or government regulation.</li>
          <li><strong>Safety & Security:</strong> We may share information to protect
            the rights, property, or safety of BloomPal, our users, or others.</li>
        </ul>

        <h3>Non-PHI Data</h3>
        <p>
          Non-PHI data (such as usage analytics) may be shared with analytics providers
          only when you have provided explicit consent.
        </p>
      </section>

      <section id="data-security">
        <h2>Data Security</h2>
        <p>
          We implement comprehensive administrative, physical, and technical safeguards
          to protect your data:
        </p>
        <ul>
          <li><strong>Encryption:</strong> All data is encrypted in transit (using TLS)
            and at rest (using industry-standard encryption algorithms).</li>
          <li><strong>Secure Servers:</strong> We use secure, HIPAA-compliant cloud
            infrastructure with regular security audits.</li>
          <li><strong>Access Controls:</strong> We limit access to your data to
            authorized personnel only, and all access is logged and monitored.</li>
          <li><strong>Regular Audits:</strong> We conduct regular security assessments
            and vulnerability testing.</li>
          <li><strong>Incident Response:</strong> We have procedures in place to detect,
            respond to, and mitigate security incidents.</li>
        </ul>
        <p>
          While we strive to protect your data, no method of transmission over the
          internet or electronic storage is 100% secure. We cannot guarantee absolute
          security, but we are committed to maintaining the highest standards of data
          protection.
        </p>
      </section>

      <section id="your-rights">
        <h2>Your Rights</h2>
        <p>
          Under HIPAA and applicable privacy laws, you have the following rights
          regarding your personal and health information:
        </p>
        <ul>
          <li><strong>Right to Access:</strong> You have the right to access and receive
            a copy of your Protected Health Information (PHI).</li>
          <li><strong>Right to Correction:</strong> You may request corrections to your
            PHI if you believe it is inaccurate or incomplete.</li>
          <li><strong>Right to Accounting:</strong> You have the right to receive an
            accounting of disclosures of your PHI.</li>
          <li><strong>Right to Restriction:</strong> You can request restrictions on
            certain uses and disclosures of your PHI, though we may decline in certain
            cases as permitted by law.</li>
          <li><strong>Right to Revoke Consent:</strong> You may revoke consent or
            authorization at any time by visiting the Privacy Settings page or
            contacting us directly.</li>
          <li><strong>Right to Deletion:</strong> You may request deletion of your
            account and associated data, subject to legal and operational requirements.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at{' '}
          <a href="mailto:hello@bloompal-wellness.com">hello@bloompal-wellness.com</a>.
        </p>
      </section>

      <section id="cookies-tracking">
        <h2>Cookies & Tracking</h2>
        <p>
          We use essential cookies for site functionality, such as maintaining your
          login session and remembering your preferences.
        </p>
        <p>
          Non-essential cookies (analytics, personalization) require your explicit
          consent via our cookie banner. You can change your preferences in your privacy
          settings at any time.
        </p>
        <p>
          We do not use tracking technologies for advertising purposes or to share your
          data with third-party advertisers.
        </p>
      </section>

      <section id="changes">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this policy from time to time to reflect changes in legal
          requirements, our service offerings, or best practices. The{' '}
          <strong>effective date</strong> at the top of this page indicates when the
          current version went into effect.
        </p>
        <p>
          If we make material changes to this policy, we will:
        </p>
        <ul>
          <li>Notify you via email or through a prominent notice on our platform.</li>
          <li>Require you to re-consent to the updated policy before continuing to use
            our services.</li>
        </ul>
        <p>
          We encourage you to review this policy periodically to stay informed about how
          we protect your information.
        </p>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy, wish to exercise your rights,
          or have concerns about how we handle your data, please contact us:
        </p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:hello@bloompal-wellness.com">hello@bloompal-wellness.com</a>
        </p>
        <p>
          We will respond to your inquiry within 30 days.
        </p>
      </section>
    </LegalLayout>
  );
}
